/** Speed against distance, for the lap and for what it is measured against.
 *
 * Distance rather than time along the bottom, which is the one decision in
 * this card that matters. Two laps plotted against time drift apart after the
 * first corner and every later difference is really the same difference
 * repeated; plotted against distance they stay in register, so a gap at
 * 2 600 m is a gap at the same corner on both laps and the chart can be read
 * across.
 *
 * Both traces share one pair of scales for the same reason, and the dashed
 * verticals are the chosen lap's sector boundaries, so the chips above and the
 * chart below are describing the same three stretches of circuit.
 */

import { Card, ErrorNote, Kicker, Spinner } from '@/components/ui';
import { DASH, count } from '@/lib/format';
import { speedIn, speedLabel, speedWords } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';
import { SPEED_BOX, distanceTicks, speedDomain, speedPolyline } from '@/components/sessions/geometry';
import type { SpeedTrace } from '@/components/sessions/geometry';
import { CornerAxis, CornerLines } from '@/components/sessions/CornerAxis';
import { cornerTicks } from '@/components/sessions/compare';
import type { CornerMark } from '@/components/sessions/compare';
import type { Reference } from '@/components/sessions/references';

export interface SpeedCardProps {
  lapNumber: number | null;
  trace: SpeedTrace | null;
  referenceTrace: SpeedTrace | null;
  reference: Reference | null;
  /** Where the chosen lap's sectors end, in metres along the lap. */
  sectorDistances: readonly number[];
  /** The corners along the axis, when a comparison has placed them. */
  corners?: readonly CornerMark[];
  peakKmh: number | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}

export function SpeedCard({
  lapNumber,
  trace,
  referenceTrace,
  reference,
  sectorDistances,
  corners = [],
  peakKmh,
  loading,
  error,
  onRetry,
}: SpeedCardProps) {
  const domain = speedDomain([trace, referenceTrace]);
  const ticks = domain ? distanceTicks(domain.maxDistance) : [];
  const cornerMarks = domain ? cornerTicks(corners, domain.maxDistance, SPEED_BOX) : [];
  // The trace itself stays in km/h — the chart has no speed axis to relabel —
  // so the unit switch is the peak figure, which is the one number read.
  const speedUnit = useSpeedUnit();
  const peak = peakKmh === null ? null : Math.round(speedIn(peakKmh, speedUnit.unit));

  return (
    <Card elevation="none" style={{ padding: 12, gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          speed · lap {lapNumber ?? DASH}
          {reference ? ` vs ${reference.label}` : ''}
        </Kicker>
        <button
          type="button"
          className="tabular-nums"
          onClick={speedUnit.toggle}
          title={`show ${speedLabel(speedUnit.other)}`}
          aria-label={`peak ${peak === null ? 'unknown' : peak} ${speedWords(speedUnit.unit)}; switch to ${speedWords(speedUnit.other)}`}
          style={{
            fontSize: 11,
            color: 'var(--color-neutral-500)',
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          peak {peak === null ? DASH : count(peak)} {speedLabel(speedUnit.unit)}
        </button>
      </div>

      {error ? (
        <ErrorNote compact error={error} title="could not draw the speed trace" onRetry={onRetry} />
      ) : !domain ? (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 120,
            fontSize: 12.5,
            color: 'var(--color-neutral-600)',
            textAlign: 'center',
          }}
        >
          {loading ? (
            <Spinner label="loading the lap" />
          ) : (
            'This lap has no speed channel stored, so there is nothing to plot against distance.'
          )}
        </div>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${SPEED_BOX.width} ${SPEED_BOX.height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Speed against distance for lap ${lapNumber ?? ''}, peak ${
              peak === null ? 'unknown' : peak
            } ${speedWords(speedUnit.unit)}`}
            style={{ width: '100%', height: SPEED_BOX.height, display: 'block' }}
          >
            <CornerLines ticks={cornerMarks} height={SPEED_BOX.height} />
            <line
              x1={0}
              y1={SPEED_BOX.height - 1}
              x2={SPEED_BOX.width}
              y2={SPEED_BOX.height - 1}
              stroke="var(--color-divider)"
              vectorEffect="non-scaling-stroke"
            />
            {sectorDistances.map((metres) => {
              const x = (metres / domain.maxDistance) * SPEED_BOX.width;
              return (
                <line
                  key={metres}
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={SPEED_BOX.height}
                  stroke="var(--color-divider)"
                  strokeDasharray="2 4"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
            {referenceTrace && reference ? (
              <polyline
                points={speedPolyline(referenceTrace, domain)}
                fill="none"
                stroke={reference.colour}
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
            <polyline
              points={speedPolyline(trace, domain)}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth={1.6}
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          <div
            className="tabular-nums"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10,
              color: 'var(--color-neutral-600)',
              marginTop: 4,
            }}
          >
            {ticks.map((metres, index) => (
              <span key={index}>
                {count(Math.round(metres))}
                {index === 0 || index === ticks.length - 1 ? ' m' : ''}
              </span>
            ))}
          </div>
          <CornerAxis ticks={cornerMarks} width={SPEED_BOX.width} />
        </>
      )}
    </Card>
  );
}

export default SpeedCard;
